import { useCallback, useRef, useState } from 'react';

import { loadBundleFormConfigPrefs, saveBundleFormConfigPrefs } from './bundle-form-config-storage';
import type { BundleItem } from './bundle-item-row';
import { BundleContentsEditor } from './bundle-preview-editor';
import { buildDefaultBundleDraft } from './bundle-preview-utils';
import { ConfigTab, type BundleConfig } from './config-tab';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useExportBundle from '@/api/mutations/useExportBundle';
import {
  LARGE_BUNDLE_BLOCK_THRESHOLD,
  LARGE_BUNDLE_WARNING_THRESHOLD,
  LargeExportBlock,
  LargeExportWarning,
} from '@/new/components/download/fields/large-export-warning';
import type {
  BundleVariantConfig,
  DownloadFormValues,
  DownloadTracking,
  DownloadTrackingPayload,
} from '@/new/components/download/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface BundleContentProps {
  variantId: string;
  config: BundleVariantConfig;
  tracking?: DownloadTracking;
  onClose: () => void;
  onBack?: () => void;
  onAsyncStarted?: (payload: {
    variantId: string;
    exportId: string;
    jobId?: string;
    format: DownloadFormValues['format'];
    bundleName?: string;
  }) => void;
  draftState?: BundleDraftState;
  onDraftStateChange?: (draft: BundleDraftState) => void;
}

export interface BundleDraftState {
  /** `null` means the user hasn't edited the item list yet; fall back to the seed items. */
  items: BundleItem[] | null;
  formConfig: BundleConfig;
}

export const BundleContent = ({
  variantId,
  config: bundleConfig,
  tracking,
  onClose,
  onBack,
  onAsyncStarted,
  draftState,
  onDraftStateChange,
}: BundleContentProps) => {
  const { items: seedItems, isLoading: isLoadingItems, isError: isItemsError } = bundleConfig.useItems();
  // `false` sentinel = not yet read. After load, `current` becomes either the
  // parsed prefs object or `null` (no prefs stored) — both terminate the read.
  const persistedPrefsRef = useRef<Partial<BundleConfig> | null | false>(false);
  if (persistedPrefsRef.current === false) {
    persistedPrefsRef.current = loadBundleFormConfigPrefs();
  }
  const persistedPrefs = persistedPrefsRef.current || {};

  const defaultDraft = buildDefaultBundleDraft(bundleConfig, seedItems);
  const items = draftState?.items ?? defaultDraft.items;
  const formConfig = {
    ...defaultDraft.formConfig,
    ...persistedPrefs,
    ...(draftState?.formConfig ?? {}),
  };

  const { exportMutation } = useExportBundle();
  const [largeBundleAcknowledged, setLargeBundleAcknowledged] = useState(false);

  const itemCount = items.length;
  const showLargeBundleBlock = itemCount > LARGE_BUNDLE_BLOCK_THRESHOLD;
  const showLargeBundleWarning = !showLargeBundleBlock && itemCount > LARGE_BUNDLE_WARNING_THRESHOLD;

  const updateDraft = useCallback(
    (nextDraft: BundleDraftState) => {
      onDraftStateChange?.(nextDraft);
    },
    [onDraftStateChange],
  );

  const handleItemsChange = useCallback(
    (nextItems: BundleItem[]) => {
      updateDraft({ items: nextItems, formConfig });
    },
    [formConfig, updateDraft],
  );

  const handleConfigChange = useCallback(
    (nextConfig: BundleConfig) => {
      saveBundleFormConfigPrefs(nextConfig);
      updateDraft({ items: draftState?.items ?? null, formConfig: nextConfig });
    },
    [draftState?.items, updateDraft],
  );

  const handleExport = async () => {
    if (items.length === 0 || !formConfig.name.trim()) return;
    if (showLargeBundleBlock) return;
    if (showLargeBundleWarning && !largeBundleAcknowledged) return;

    const { name, ...config } = formConfig;

    if (tracking) {
      const payload: DownloadTrackingPayload = {
        ...tracking.basePayload,
        variant: 'bundle',
        itemCount: items.length,
      };
      trackCustomEvent(tracking.event, payload);
    }

    try {
      const exportResult = await exportMutation.mutateAsync({
        matterId: bundleConfig.matterId,
        subsetId: bundleConfig.subsetId,
        name,
        config,
        items: items.map(({ schemaName, schemaId, customName, dateText }) => ({
          schemaName,
          schemaId,
          customName: customName ?? undefined,
          dateText: dateText ?? undefined,
        })),
      });

      onClose();
      onAsyncStarted?.({
        variantId,
        exportId: exportResult.exportId,
        jobId: exportResult.jobId,
        format: 'pdf' as DownloadFormValues['format'],
        bundleName: name,
      });
    } catch {
      // Mutation hook surfaces the toast.
    }
  };

  const isExporting = exportMutation.isLoading;

  const disabledReason = showLargeBundleBlock
    ? `This bundle contains ${itemCount.toLocaleString()} items. Bundles are limited to ${LARGE_BUNDLE_BLOCK_THRESHOLD.toLocaleString()} items; remove items and try again.`
    : showLargeBundleWarning && !largeBundleAcknowledged
      ? 'Please acknowledge that this bundle may take a while before continuing.'
      : undefined;

  const isExportDisabled =
    isLoadingItems || isItemsError || items.length === 0 || !formConfig.name.trim() || isExporting || !!disabledReason;

  return (
    <div className="flex min-h-0 flex-col">
      {(showLargeBundleBlock || showLargeBundleWarning) && (
        <div className="mb-3 shrink-0">
          {showLargeBundleBlock ? (
            <LargeExportBlock totalCount={itemCount} blockThreshold={LARGE_BUNDLE_BLOCK_THRESHOLD} />
          ) : (
            <LargeExportWarning
              totalCount={itemCount}
              acknowledged={largeBundleAcknowledged}
              onAcknowledgedChange={setLargeBundleAcknowledged}
            />
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-row gap-4 overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col">
          {isLoadingItems ? (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
              <Icon className="lucide--loader-circle mr-2 animate-spin" />
              Loading bundle items...
            </div>
          ) : isItemsError ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-red-600">
              <Icon className="lucide--triangle-alert" />
              Failed to load bundle items. The selection may be too large to fetch in one request. Please try again or
              reduce the number of items.
            </div>
          ) : seedItems.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-gray-500">
              <Icon className="lucide--file-x" />
              No source documents were found for this scope.
            </div>
          ) : (
            <BundleContentsEditor
              items={items}
              config={formConfig}
              onItemsChange={handleItemsChange}
              onConfigChange={handleConfigChange}
            />
          )}
        </div>

        <div className="w-full max-w-sm shrink-0 overflow-y-auto p-2">
          <ConfigTab config={formConfig} onConfigChange={handleConfigChange} />
        </div>
      </div>

      <div className="mt-5 flex shrink-0 justify-between gap-2">
        <div>
          {onBack && (
            <Button variant="ghost" onClick={onBack}>
              <Icon className="lucide--chevron-left" />
              Back
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="primary" onClick={handleExport} disabled={isExportDisabled}>
                  {isExporting ? 'Creating...' : 'Download as PDF'}
                </Button>
              </span>
            </TooltipTrigger>
            {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
